import { HIGHLIGHTED_CLASS_NAME, HIGHLIGHT_COLOR_PALETTE } from '../../consts'
import { cloneDeep, uniq } from 'lodash'
import { convertHexColorToRgbWithOpacity } from './convert-hex-color-to-rgb-with-opacity'
import type { ColoredHighlight } from './types'

export interface HighlightConfig {
  className?: string
  style?: React.CSSProperties
  word: string
  exact?: boolean
}

export const highlightKeywords = (
  text: string,
  originalColoredHighlights: ColoredHighlight[]
): string => {
  /*
  This function highlights keywords in the text.
  It uses a mapping of character indices to keyword indices.
  A highlight index of -1 means no highlight.

  We map to keyword indices instead of colors directly to properly separate
  matches belonging to different keywords that have the same color.
  */

  const coloredHighlights = cloneDeep(originalColoredHighlights)

  if (coloredHighlights.length === 0) {
    return text
  }

  // -1 means no highlight.
  const charHighlightIndexMapping = new Int8Array(text.length).fill(-1)

  for (const [index, { regex }] of coloredHighlights.entries()) {
    const matches = text.matchAll(regex)
    for (const match of matches) {
      for (let charOffset = 0; charOffset < match[0].length; charOffset++) {
        charHighlightIndexMapping[match.index + charOffset] = index
      }
    }
  }

  let highlightedText = ''
  let lastUsedHighlightIndex = -1

  for (let charIndex = 0; charIndex < text.length; charIndex++) {
    const highlightIndex = charHighlightIndexMapping[charIndex]!

    if (highlightIndex === lastUsedHighlightIndex) {
      // Char before and this one are highlighted by the same keyword (with special case for last
      // char).
      const isLastColoredChar =
        charIndex === text.length - 1 && highlightIndex !== -1
      highlightedText += `${text[charIndex]}${isLastColoredChar ? '</span>' : ''}`
      continue
    }

    const colorIndex = coloredHighlights[highlightIndex]?.colorIndex ?? -1
    let textToAdd = ''

    if (lastUsedHighlightIndex === -1) {
      // Char before is not highlighted.
      textToAdd = `<span 
        class='${HIGHLIGHTED_CLASS_NAME} ${HIGHLIGHTED_CLASS_NAME}-${colorIndex}' 
        style='
          background-color: ${convertHexColorToRgbWithOpacity(HIGHLIGHT_COLOR_PALETTE[colorIndex]!, 0.5)}'>${text[charIndex]}`
    } else if (highlightIndex === -1) {
      // Char before is highlighted, but this char is not highlighted.
      textToAdd = `</span>${text[charIndex]}`
    } else {
      // Char before is highlighted, and this char is highlighted as well, but by a different
      // keyword.
      textToAdd = `</span><span 
        class='${HIGHLIGHTED_CLASS_NAME} ${HIGHLIGHTED_CLASS_NAME}-${colorIndex}' 
        style='
          background-color: ${convertHexColorToRgbWithOpacity(HIGHLIGHT_COLOR_PALETTE[colorIndex]!, 0.5)}'>${text[charIndex]}`
    }

    highlightedText += textToAdd
    lastUsedHighlightIndex = highlightIndex
  }

  return highlightedText
}

export const createHighlightKeywordsConfig = (
  text: string,
  coloredHighlights: ColoredHighlight[]
): HighlightConfig[] => {
  const highlightMap = new Map<number, Array<[string, number]>>()

  for (const [
    highlightIndex,
    { regex, colorIndex },
  ] of coloredHighlights.entries()) {
    const matches = text.matchAll(regex)
    const matchesArray: Array<[string, number]> = [...matches].map((match) => [
      match[0],
      highlightIndex,
    ])
    const existingMatches = highlightMap.get(colorIndex) ?? []
    highlightMap.set(colorIndex, [...existingMatches, ...matchesArray])
  }

  const highlightConfigs: HighlightConfig[] = [
    ...highlightMap.entries(),
  ].flatMap(([colorIndex, matches]) => {
    return uniq(matches).map(([match, highlightIndex]) => ({
      word: match,
      exact: true,
      className: `${HIGHLIGHTED_CLASS_NAME} ${HIGHLIGHTED_CLASS_NAME}-${highlightIndex}`,
      style: {
        backgroundColor: convertHexColorToRgbWithOpacity(
          HIGHLIGHT_COLOR_PALETTE[colorIndex]!,
          0.5
        ),
      },
    }))
  })

  return highlightConfigs
}
